import type { ReactNode } from 'react';
import { ConfirmDialog } from '@/components/ui';
import type { MasterScreenState } from '@/hooks/useMasterScreenState';

/**
 * The three questions every master screen asks: create this code, save this, delete this.
 *
 * All eight screens rendered the same three ConfirmDialogs with the same variants, labels and
 * handlers, and each had written the sentences slightly differently. The create question alone
 * came in three phrasings — "Create a new record?", "Do you want to create a new record?" and one
 * that asked on a second line — and only two of the eight mentioned that a delete is a soft delete,
 * which is true on all of them. The same operation was described differently depending on which
 * master you happened to be standing on.
 *
 * The wording lives here now, so it is one sentence per question everywhere.
 *
 * A screen with a question of its own — the financial period's close and reopen — still renders its
 * own dialog alongside this. This owns the three that are common, not every dialog on the screen.
 */

interface CreateQuestion {
  /**
   * What the user typed, shown in the question — a code, or a composite key like
   * "AED → LKR on 2055-03-01". `null` closes the dialog.
   */
  label: ReactNode;
  /** Anything else worth knowing before they say yes, e.g. how the record will be named. */
  note?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

interface MasterConfirmDialogsProps<TDto> {
  /** The entity in title case, as it appears in a heading: "Bank", "Cost centre". */
  entity: string;
  screen: MasterScreenState;
  record: TDto | null;
  /** How the record is named back to the user: usually its code and its name. */
  describe: (record: TDto) => ReactNode;
  saving: boolean;

  create: CreateQuestion;

  saveOpen: boolean;
  onSaveConfirm: () => void;
  onSaveCancel: () => void;

  deleteOpen: boolean;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
}

export function MasterConfirmDialogs<TDto>({
  entity, screen, record, describe, saving,
  create,
  saveOpen, onSaveConfirm, onSaveCancel,
  deleteOpen, onDeleteConfirm, onDeleteCancel,
}: MasterConfirmDialogsProps<TDto>) {
  // "Bank" heads a dialog; "bank" sits inside a sentence.
  const noun = entity.toLowerCase();

  return (
    <>
      <ConfirmDialog
        isOpen={create.label !== null}
        variant="info"
        title={`Create New ${entity}`}
        message={
          <span>
            {entity} <strong className="font-mono">{create.label}</strong> does not exist.
            <br />Do you want to create a new record?
            {create.note && (
              <span className="text-xs text-gray-400 mt-1 block">{create.note}</span>
            )}
          </span>
        }
        confirmLabel="Yes, Create"
        cancelLabel="No"
        onConfirm={create.onConfirm}
        onCancel={create.onCancel}
      />

      <ConfirmDialog
        isOpen={saveOpen}
        variant="save"
        title={screen.isNew ? `Save New ${entity}` : 'Save Changes'}
        message={
          screen.isNew
            ? `Do you want to save this new ${noun}?`
            : <span>Do you want to save changes to {record && <strong>{describe(record)}</strong>}?</span>
        }
        confirmLabel="Yes, Save"
        cancelLabel="No"
        loading={saving}
        onConfirm={onSaveConfirm}
        onCancel={onSaveCancel}
      />

      <ConfirmDialog
        isOpen={deleteOpen}
        variant="delete"
        title={`Delete ${entity}`}
        message={
          <span>
            Do you want to delete {noun} {record && <strong>{describe(record)}</strong>}?
            {/* True of every master, and previously mentioned on only two of them. */}
            <span className="text-xs text-gray-400 mt-1 block">
              The record is soft-deleted. Anything that already refers to it keeps its code.
            </span>
          </span>
        }
        confirmLabel="Yes, Delete"
        cancelLabel="No"
        loading={saving}
        onConfirm={onDeleteConfirm}
        onCancel={onDeleteCancel}
      />
    </>
  );
}
